import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import sha from 'sha.js';
import { TextInput, Clickable, Text, Stack } from '@moda/om';
import ChevronRightIcon from '@moda/icons/chevron-right-20';
import { hrefFor } from '../../../routers';
import { Link } from '../../Link';
import { useEmailSubscribe } from '../../../hooks/useEmailSubscribe';

import './SiteFooterMailingList.scss';

export const SiteFooterMailingList: FC<{ className?: string }> = ({ className }) => {
  const [email, setEmail] = useState<string>('');

  const {
    subscribe,
    isSubscribed,
    isExistingSubscription,
    error: mutationError,
    mailValidationError
  } = useEmailSubscribe();

  const error = mutationError || mailValidationError;

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();

      subscribe(email, 'footer');
    },
    [email, subscribe]
  );

  return (
    <Stack className={classNames('SiteFooterMailingList__wrapper', className)} space={2}>
      <h3 className="SiteFooterMailingList__title">
        <Text treatment="eyebrow">
          Join our list for daily style inspiration &amp; first access to global fashion.
        </Text>
      </h3>

      <form className={classNames('SiteFooterMailingList')} onSubmit={handleSubmit}>
        <div className="SiteFooterMailingList__input">
          <TextInput
            disabled={isSubscribed}
            error={!!error}
            onChange={({ currentTarget: { value } }) => setEmail(value)}
            placeholder={isSubscribed ? 'Email submitted!' : 'Sign up for our newsletter'}
            required
            type="email"
            value={isSubscribed ? '' : email}
          />

          {!isSubscribed && (
            <Clickable
              aria-label="Submit email for our newsletter"
              className="SiteFooterMailingList__button"
              disabled={isSubscribed}
              type="submit"
            >
              <ChevronRightIcon />
            </Clickable>
          )}
        </div>

        {error && (
          <div
            className={classNames('SiteFooterMailingList__status', {
              'SiteFooterMailingList__status--error': error
            })}
          >
            {mailValidationError || 'Unable to subscribe you at this time'}
          </div>
        )}

        {isExistingSubscription && (
          <div
            className={classNames(
              'SiteFooterMailingList__status',
              'SiteFooterMailingList__status--success'
            )}
          >
            You're already on our newsletter list,{' '}
            <Link
              className="SiteFooterMailingList__manage-subscriptions-link"
              to={`${hrefFor.UnsubscribePage()}?email_hash=${sha('sha256')
                .update(email)
                .digest('hex')}`}
            >
              click here
            </Link>{' '}
            to manage your subscriptions.
          </div>
        )}

        <div className="SiteFooterMailingList__opt-out">
          To opt-out of email sharing or to view our Privacy Policy,{' '}
          <Link className="SiteFooterMailingList__opt-out-link" to="/privacy">
            click here
          </Link>
        </div>
      </form>
    </Stack>
  );
};
